"use client";

import { useEffect, useMemo, useState } from "react";

import {
  UpcomingPortfolioSummary,
  type UpcomingPortfolioItem,
} from "@/components/portfolio/UpcomingPortfolioSummary";
import { useAppContext } from "@/context/AppContext";
import { useEstateContext } from "@/context/EstateContext";
import { useLanguage } from "@/context/LanguageContext";
import {
  getNextPortfolioRecordOccurrence,
  getPortfolioRecords,
  type PortfolioRecord,
} from "@/lib/portfolio-records";
import type { Transaction } from "@/types";

const DAY_IN_MS = 86_400_000;

type LoadedRecord = {
  estateName: string;
  record: PortfolioRecord;
};

function atLocalNoon(value: Date) {
  return new Date(
    value.getFullYear(),
    value.getMonth(),
    value.getDate(),
    12,
  );
}

function addMonthsClamped(base: Date, count: number) {
  const target = new Date(
    base.getFullYear(),
    base.getMonth() + count + 1,
    0,
    12,
  );
  return new Date(
    target.getFullYear(),
    target.getMonth(),
    Math.min(base.getDate(), target.getDate()),
    12,
  );
}

function getNextTransactionOccurrence(transaction: Transaction, today: Date) {
  const start = atLocalNoon(new Date(transaction.dateFrom));
  const end = transaction.dateTo
    ? atLocalNoon(new Date(transaction.dateTo))
    : null;
  let next: Date;

  if (transaction.recurrence === "MONTHLY") {
    const elapsedMonths =
      (today.getFullYear() - start.getFullYear()) * 12 +
      today.getMonth() -
      start.getMonth();
    next = addMonthsClamped(start, Math.max(0, elapsedMonths));
    if (next < today) next = addMonthsClamped(start, elapsedMonths + 1);
  } else if (transaction.recurrence === "YEARLY") {
    const elapsedYears = Math.max(0, today.getFullYear() - start.getFullYear());
    next = new Date(
      start.getFullYear() + elapsedYears,
      start.getMonth(),
      start.getDate(),
      12,
    );
    if (next < today) {
      next = new Date(
        next.getFullYear() + 1,
        start.getMonth(),
        start.getDate(),
        12,
      );
    }
  } else {
    next = start;
  }

  if (next < today || (end && next > end)) return null;
  return next;
}

export function HomeUpcomingPortfolioSummary() {
  const { userInfoGlobal } = useAppContext();
  const { allEstateList, isLoadingEstate } = useEstateContext();
  const { language } = useLanguage();
  const [records, setRecords] = useState<LoadedRecord[]>([]);
  const [isLoadingRecords, setIsLoadingRecords] = useState(false);

  useEffect(() => {
    let cancelled = false;

    if (
      !userInfoGlobal?.email ||
      !userInfoGlobal.token ||
      allEstateList.length === 0
    ) {
      setRecords([]);
      setIsLoadingRecords(false);
      return;
    }

    setIsLoadingRecords(true);
    const user = {
      email: userInfoGlobal.email,
      token: userInfoGlobal.token,
    };

    Promise.allSettled(
      allEstateList.map(async (estate) => ({
        estateName: estate.name,
        records: await getPortfolioRecords(estate.id, user),
      })),
    )
      .then((results) => {
        if (cancelled) return;
        setRecords(
          results.flatMap((result) =>
            result.status === "fulfilled"
              ? result.value.records.map((record) => ({
                  estateName: result.value.estateName,
                  record,
                }))
              : [],
          ),
        );
      })
      .finally(() => {
        if (!cancelled) setIsLoadingRecords(false);
      });

    return () => {
      cancelled = true;
    };
  }, [allEstateList, userInfoGlobal?.email, userInfoGlobal?.token]);

  const items = useMemo(() => {
    const today = atLocalNoon(new Date());
    const rangeEnd = new Date(today.getTime() + 30 * DAY_IN_MS);
    const locale = language === "cs" ? "cs-CZ" : "en-GB";
    const formatDate = (date: Date) =>
      new Intl.DateTimeFormat(locale, {
        day: "numeric",
        month: "long",
      }).format(date);
    const formatAmount = (amount: number) =>
      new Intl.NumberFormat(locale, {
        style: "currency",
        currency: "CZK",
        maximumFractionDigits: 0,
      }).format(Math.abs(amount));
    const datedItems: Array<{ date: Date; item: UpcomingPortfolioItem }> = [];

    allEstateList.forEach((estate) => {
      estate.transactions
        .filter((transaction) => transaction.showInDashboard !== false)
        .forEach((transaction) => {
          const date = getNextTransactionOccurrence(transaction, today);
          if (!date || date > rangeEnd) return;

          datedItems.push({
            date,
            item: {
              id: `transaction-${estate.id}-${transaction.originalId}-${date.toISOString()}`,
              type: "transaction",
              title: `${transaction.name} · ${estate.name}`,
              date: formatDate(date),
              value: formatAmount(transaction.amount),
            },
          });
        });
    });

    records.forEach(({ estateName, record }) => {
      const date = getNextPortfolioRecordOccurrence(record, today);
      if (!date || date > rangeEnd) return;

      datedItems.push({
        date,
        item: {
          id: `record-${record.estateId}-${record.id}-${date.toISOString()}`,
          type: "record",
          hasReminder: Boolean(record.reminderAmount && record.reminderUnit),
          title: `${record.title} · ${estateName}`,
          date: formatDate(date),
          value:
            record.reminderAmount && record.reminderUnit
              ? language === "cs"
                ? "Připomenutí"
                : "Reminder"
              : record.documents.length > 0
                ? language === "cs"
                  ? "Dokument"
                  : "Document"
                : language === "cs"
                  ? "Termín"
                  : "Deadline",
        },
      });
    });

    return datedItems
      .sort((left, right) => left.date.getTime() - right.date.getTime())
      .slice(0, 6)
      .map(({ item }) => item);
  }, [allEstateList, language, records]);

  return (
    <UpcomingPortfolioSummary
      compact
      language={language}
      items={items}
      isLoading={isLoadingEstate || isLoadingRecords}
    />
  );
}
